/**
 * Cart recommendations: recommends products based on cart, purchase history, and browsing history.
 * Fetches recommendations per seed product and merges/dedupes results.
 */
const RECENTLY_VIEWED_KEY = 'cart_recently_viewed_ids';
const MAX_RECENTLY_VIEWED = 10;

function parseIdList(value) {
  if (!value || typeof value !== 'string') return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((id) => /^\d+$/.test(id));
}

function uniq(array) {
  return [...new Set(array)];
}

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.loadingEl = this.querySelector('.cart-recommendations__loading');
    this.contentEl = this.querySelector('.cart-recommendations__content');
    this.emptyEl = this.querySelector('.cart-recommendations__empty');
    this.gridEl = this.contentEl?.querySelector('.cart-recommendations__grid');
    this.headingEl = this.contentEl?.querySelector('.cart-recommendations__heading');
  }

  connectedCallback() {
    this.loadRecommendations();
  }

  getRecommendationsUrl() {
    const base = this.dataset.recommendationsUrl;
    if (!base) return null;
    const sectionId = this.dataset.sectionId;
    const limit = this.dataset.limit || '4';
    return `${base}?limit=${limit}`;
  }

  getSeedProductIds() {
    const cartIds = parseIdList(this.dataset.cartProductIds || '');
    const orderIds = parseIdList(this.dataset.recentOrderProductIds || '');
    let viewedIds = [];
    try {
      const stored = localStorage.getItem(RECENTLY_VIEWED_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        viewedIds = Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'number' || /^\d+$/.test(String(id))) : [];
      }
    } catch (_) {
      viewedIds = [];
    }

    const all = [...cartIds, ...orderIds, ...viewedIds];
    const seen = new Set(cartIds);
    const seeds = [];
    for (const id of all) {
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      seeds.push(key);
    }
    const maxSeeds = parseInt(this.dataset.maxSeedProducts, 10) || 3;
    return seeds.slice(0, maxSeeds);
  }

  async loadRecommendations() {
    const seeds = this.getSeedProductIds();
    const urlBase = this.getRecommendationsUrl();
    const sectionId = this.dataset.sectionId;
    const productsToShow = parseInt(this.dataset.productsToShow, 10) || 4;
    const cartIdsSet = new Set(parseIdList(this.dataset.cartProductIds || ''));

    if (!urlBase || !sectionId) {
      this.showEmpty();
      return;
    }

    if (seeds.length === 0) {
      this.showEmpty();
      return;
    }

    const collected = [];
    const seenProductIds = new Set();

    try {
      for (const productId of seeds) {
        const url = `${urlBase}&section_id=${sectionId}&product_id=${productId}`;
        const response = await fetch(url);
        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const batch = doc.querySelector('.cart-recommendations__batch');
        if (!batch) continue;
        const items = batch.querySelectorAll('.grid__item[data-product-id]');
        for (const item of items) {
          const pid = item.getAttribute('data-product-id');
          if (!pid || seenProductIds.has(pid) || cartIdsSet.has(pid)) continue;
          seenProductIds.add(pid);
          collected.push({ node: item, productId: pid });
          if (collected.length >= productsToShow) break;
        }
        if (collected.length >= productsToShow) break;
      }

      if (collected.length === 0) {
        this.showEmpty();
        return;
      }

      this.render(collected);
    } catch (e) {
      console.error('Cart recommendations error:', e);
      this.showEmpty();
    }
  }

  render(items) {
    if (!this.loadingEl || !this.contentEl || !this.gridEl) return;
    this.loadingEl.classList.add('hidden');
    this.emptyEl?.classList.add('hidden');
    this.contentEl.classList.remove('hidden');

    const heading = this.dataset.heading || 'You may also like';
    const size = this.dataset.headingSize || 'h1';
    if (this.headingEl) {
      this.headingEl.textContent = heading;
      this.headingEl.className = `cart-recommendations__heading inline-richtext ${size}`;
    }

    this.gridEl.innerHTML = '';
    for (const { node } of items) {
      this.gridEl.appendChild(node.cloneNode(true));
    }
  }

  showEmpty() {
    if (this.loadingEl) this.loadingEl.classList.add('hidden');
    if (this.contentEl) this.contentEl.classList.add('hidden');
    if (this.emptyEl) this.emptyEl.classList.remove('hidden');
  }
}

customElements.define('cart-recommendations', CartRecommendations);

/**
 * Browsing history: add current product ID to recently viewed (called from product page).
 * Usage: CartRecommendationsTrack?.track(productId)
 */
window.CartRecommendationsTrack = {
  track(productId) {
    if (!productId) return;
    try {
      const key = RECENTLY_VIEWED_KEY;
      let list = [];
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          list = Array.isArray(parsed) ? parsed : [];
        } catch (_) {}
      }
      const id = typeof productId === 'number' ? productId : parseInt(String(productId), 10);
      if (Number.isNaN(id)) return;
      list = list.filter((x) => x !== id);
      list.push(id);
      if (list.length > MAX_RECENTLY_VIEWED) list = list.slice(-MAX_RECENTLY_VIEWED);
      localStorage.setItem(key, JSON.stringify(list));
    } catch (_) {}
  },
};
